export type CommandFormSnapshotInput = {
  id?: string
  name: string
  description: string
  prompt: string
  icon_key: string
  channel: string
  execution_mode: string
  model_key: string | null
  allowed_tools: string[]
  chat_invocable: boolean
  chat_invocation_hint: string
  deterministic_graph: Record<string, unknown> | null
  trigger_type: string
  trigger_config: Record<string, unknown>
  delivery_target: string
  email_recipients: string[]
  email_subject: string
  max_runs_per_hour: number
  max_runs_per_day: number
  enabled: boolean
}

export type CommandFormSnapshot = Omit<CommandFormSnapshotInput, 'id' | 'prompt' | 'allowed_tools' | 'deterministic_graph'> & {
  id?: string
  prompt: string
  allowed_tools: string[] | string
  deterministic_graph: Record<string, unknown> | string | null
}

const PROMPT_LIMIT = 1000
const GRAPH_LIMIT = 1500
const TOOL_PREVIEW_LIMIT = 10

export function buildCommandFormSnapshot(form: CommandFormSnapshotInput): CommandFormSnapshot {
  return {
    ...(form.id ? { id: form.id } : {}),
    name: form.name,
    description: form.description,
    prompt: truncatePrompt(form.prompt),
    icon_key: form.icon_key,
    channel: form.channel,
    execution_mode: form.execution_mode,
    model_key: form.model_key,
    allowed_tools: summarizeAllowedTools(form.allowed_tools),
    chat_invocable: form.chat_invocable,
    chat_invocation_hint: form.chat_invocation_hint,
    deterministic_graph: summarizeGraphIfNeeded(form.deterministic_graph),
    trigger_type: form.trigger_type,
    trigger_config: form.trigger_config,
    delivery_target: form.delivery_target,
    email_recipients: form.email_recipients,
    email_subject: form.email_subject,
    max_runs_per_hour: form.max_runs_per_hour,
    max_runs_per_day: form.max_runs_per_day,
    enabled: form.enabled,
  }
}

export function commandFormSnapshotPreamble(args: {
  pathname: string
  snapshot: CommandFormSnapshot
  focusedField?: string
}): string {
  const focused = args.focusedField ? `\n[Focused field: ${args.focusedField}]` : ''
  return `[Context: building a command at ${args.pathname}]${focused}\n${JSON.stringify(args.snapshot, null, 2)}`
}

function truncatePrompt(prompt: string): string {
  if (prompt.length <= PROMPT_LIMIT) return prompt
  return `${prompt.slice(0, PROMPT_LIMIT)}...`
}

function summarizeAllowedTools(tools: string[]): string[] | string {
  if (tools.length <= TOOL_PREVIEW_LIMIT) return tools
  const remaining = tools.length - TOOL_PREVIEW_LIMIT
  return `${tools.length} tools: ${tools.slice(0, TOOL_PREVIEW_LIMIT).join(', ')} ... and ${remaining} more`
}

function summarizeGraphIfNeeded(graph: Record<string, unknown> | null): Record<string, unknown> | string | null {
  if (!graph) return null
  const raw = JSON.stringify(graph)
  if (raw.length <= GRAPH_LIMIT) return graph
  const nodes = Array.isArray(graph.nodes) ? graph.nodes : []
  const kinds = nodes
    .map((node) => {
      if (!node || typeof node !== 'object' || Array.isArray(node)) return null
      return typeof (node as Record<string, unknown>).kind === 'string'
        ? String((node as Record<string, unknown>).kind)
        : null
    })
    .filter((kind): kind is string => !!kind)
  const path = kinds.length > 0 ? kinds.join(' -> ') : 'unknown'
  return `graph: ${nodes.length} steps (${path})`
}
